import React, {Component} from "react";
import "./EmrCheckPanel.css";
import {Button} from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import {Badge, NumberInput, RadioInput, SelectInput, TextareaInput, TextInput} from "../../../../components";
import ObjectUtil from "../../../../utils/ObjectUtil";
import RecommenderService from "../../../../services/recommender-service/RecommenderService";
import RemotingService from "../../../../services/remoting-service/RemotingService";
import Tooltip from "../../../../components/tooltip/Tooltip";
import LoadingSteps from "./LoadingSteps";
import Dictation from "./Dictation";
import GuidelinePanel from "./GuidelinePanel";
import {
    addedComposeValues, allComposePaths, buildComposeRows, effectiveComposeValue,
    formatComposeValue, isDestructiveRow, isEmptyComposeValue, unmappedComposePaths
} from "./ComposeFields";

const COMPOSE_STEPS = [
    "Reading the note…",
    "Pulling out onset, character and timing…",
    "Mapping each phrase onto a record field…",
    "Checking the values against the form's options…",
];

const CHECK_STEPS = [
    "Reading this visit…",
    "Matching the record against the billed codes…",
    "Running the claim rules…",
    "Looking for what a payer would deny…",
    "Drafting answers you can accept in one tap…",
];

const CPT_STEPS = [
    "Mapping this visit onto a billing request…",
    "Finding similar visits…",
    "Validating the codes…",
];

// Plain-language consequence labels: "deny" is what happens to the claim, not a severity
// grade, and the clinician should be able to read the stake without decoding jargon.
const CONSEQUENCE_META = {
    deny: {variant: "danger", label: "Can be denied", order: 0},
    downcode: {variant: "warning", label: "Can be downcoded", order: 1},
    audit: {variant: "info", label: "Audit flag", order: 2},
};

// The visit's own tabs, so a finding with no writable field can still send the
// clinician to the right place (finding.section matches these eventKeys 1:1).
const SECTION_TAB = {
    subjective: {key: "subjective", label: "Subjective"},
    diagnosis: {key: "diagnosis", label: "Diagnosis & Billing"},
    treatment: {key: "treatment", label: "Treatment"},
};

// CPT codes already on the visit: the recommender tab only shows what is missing.
const recordedCptCodes = (visit) => {
    const procedures = [
        ...(ObjectUtil.get(visit, "diagnosis.directBillingProcedures") || []),
        ...(ObjectUtil.get(visit, "diagnosis.selfPayProcedures") || []),
    ];
    const codes = procedures.map(p => String(p?.cpt?.cptCode ?? p?.cpt?.code ?? p?.cpt ?? "").trim());
    (ObjectUtil.get(visit, "diagnosis.diagnosisGroups") || []).forEach(group =>
        (group?.diagnosisItems || []).forEach(item => codes.push(String(item?.code || "").trim())));
    return new Set(codes.filter(Boolean));
};

// `id` is re-assigned to f1..fN on every analyze call, so it cannot identify a finding
// across re-checks. rule_id + target_path can.
const findingKey = f => `${f.rule_id}|${f.target_path || ""}`;

const isEmpty = v => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

// Distinguish "service is down" from "the service rejected our request" -- otherwise a
// contract mismatch (e.g. a 422 on the payload) reads as a connectivity problem.
const errorMessage = (err) => {
    const status = err?.response?.status;
    if (!status) return "Could not reach the recommender service.";
    return `Recommender returned ${status}.`;
};

const formatValue = value => {
    if (isEmpty(value)) return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

// One-tap negative confirmations. These are not claim findings — they are the
// "confirm none" half of the app's own missing-points list (MissingDataFirstVisit.js),
// which the checkers satisfy with a `none: true` flag on the parent object
// (GenericChecker.isNone) or `redFlagNormal` for red flags. They cost the clinician a
// single tap, so they are kept out of the ranked findings and never counted as questions.
// `apply` mirrors what the corresponding modal writes, including wiping the item list.
const CONFIRM_NONE = [
    {
        key: "allergy",
        label: "No known allergies",
        open: v => !ObjectUtil.get(v, "subjective.allergy.none")
            && isEmpty(ObjectUtil.get(v, "subjective.allergy.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.allergy") || {};
            ObjectUtil.set(v, ["subjective", "allergy"], {id: existing.id, none: true});
        },
    },
    {
        key: "medicationUse",
        label: "No medications",
        open: v => !ObjectUtil.get(v, "subjective.medicationUse.none")
            && isEmpty(ObjectUtil.get(v, "subjective.medicationUse.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.medicationUse") || {};
            ObjectUtil.set(v, ["subjective", "medicationUse"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "surgicalHistory",
        label: "No surgeries",
        open: v => !ObjectUtil.get(v, "subjective.surgicalHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.surgicalHistory.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.surgicalHistory") || {};
            ObjectUtil.set(v, ["subjective", "surgicalHistory"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "medicalHistory",
        label: "No significant medical history",
        open: v => !ObjectUtil.get(v, "subjective.medicalHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.medicalHistory.diagnosisItems")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.medicalHistory") || {};
            ObjectUtil.set(v, ["subjective", "medicalHistory"], {
                id: existing.id, none: true, items: [],
                // the vaccination answers live on this object and are unrelated to the flag
                vaccinationHistory: existing.vaccinationHistory,
                vaccinationHistoryDetail: existing.vaccinationHistoryDetail,
            });
        },
    },
    {
        key: "familyHistory",
        label: "No significant family history",
        open: v => !ObjectUtil.get(v, "subjective.familyHistory.none")
            && isEmpty(ObjectUtil.get(v, "subjective.familyHistory.items")),
        apply: v => {
            const existing = ObjectUtil.get(v, "subjective.familyHistory") || {};
            ObjectUtil.set(v, ["subjective", "familyHistory"], {id: existing.id, none: true, items: []});
        },
    },
    {
        key: "redFlag",
        label: "No red flags",
        open: v => ObjectUtil.get(v, "subjective.complaint.redFlagNormal") !== true
            && isEmpty(ObjectUtil.get(v, "subjective.complaint.redFlag")),
        apply: v => ObjectUtil.set(v, ["subjective", "complaint", "redFlagNormal"], true),
    },
];

export default class EmrCheckPanel extends Component {

    constructor(props) {
        super(props);
        this.state = {
            // The drawer opens on the writing step, not the checking one: a note has to
            // exist before there is anything worth checking. The Check tab carries the
            // finding count in its title so the badge on the button stays honest.
            activeTab: "draft",
            wide: true,           // the note textarea needs the room; toggled in the header
            // One draft box per record section, expanded one at a time; the guideline
            // panel follows whichever is open. The LLM routes misplaced content anyway —
            // the split is a prompt to the clinician, not a constraint on the parse.
            draftSections: {subjective: "", objective: "", treatment: ""},
            activeDraftSection: "subjective",
            composedPaths: [],    // leaf paths the last parse yielded — the guideline
                                  // panel's "captured" tier; keyword matches are live
            composing: false,
            composeSections: null,   // [{title, rows:[{path,label,oldValue,newValue,selected}]}]
            composeUnmapped: [],
            composeNote: "",
            loading: false,
            error: null,
            analysis: null,
            answers: {},          // findingKey -> value typed by the clinician
            accepted: {},         // findingKey -> bool, for suggestions
            confirmed: {},        // CONFIRM_NONE key -> bool
            expanded: {},         // findingKey -> bool, overrides the severity default
            applied: false,       // something was written but not saved yet
            recommendLoading: false,
            recommendError: null,
            recommendation: null,
            cptIndex: null,       // cptCode -> {cpt, billType}, from this clinic's catalogue
            catalogLoading: false,
            addError: null,
            topOffset: 0,         // where the app header ends, so the drawer clears the menu
        };
    }

    componentDidMount() {
        // Seed the note with the complaint already on the record so dictation continues it
        // instead of starting a second, competing account of the same visit.
        const recorded = ObjectUtil.get(this.props.currentVisit, "subjective.complaint.complaint");
        if (!isEmptyComposeValue(recorded)) {
            this.setState(state => ({draftSections: {...state.draftSections, subjective: recorded}}));
        }

        this.runAnalyze();
        document.addEventListener("keydown", this.onKeyDown);
        // The app's top navigation is static (it scrolls away), so a drawer pinned to
        // top:0 would cover the menu. Track where the header actually ends instead.
        this.trackHeaderOffset();
        window.addEventListener("scroll", this.trackHeaderOffset, true);
        window.addEventListener("resize", this.trackHeaderOffset);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("scroll", this.trackHeaderOffset, true);
        window.removeEventListener("resize", this.trackHeaderOffset);
        if (this.offsetFrame) cancelAnimationFrame(this.offsetFrame);
    }

    trackHeaderOffset = () => {
        if (this.offsetFrame || this.props.hidden) return;
        this.offsetFrame = requestAnimationFrame(() => {
            this.offsetFrame = null;
            const header = document.querySelector(".Wrapper > .Header");
            const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
            if (top !== this.state.topOffset) this.setState({topOffset: top});
        });
    };

    onKeyDown = (event) => {
        if (event.key === "Escape" && !this.props.hidden) this.requestClose();
    };

    // Closing is free: EMR.js keeps this component mounted once it has been opened, so the
    // note, the dictation and the reviewed parse are all still here on the way back in.
    // Nothing is lost, so nothing is asked — the old "Discard note?" prompt was guarding an
    // unmount that no longer happens.
    requestClose = () => {
        if (this.props.onClose) this.props.onClose();
    };

    componentDidUpdate(prevProps) {
        // EMR.js reloads the record after a save; re-check against the fresh visit. A closed
        // drawer stays mounted now, so the re-check is deferred rather than fired at a panel
        // no one is looking at — the visit can be saved several times while it sits closed.
        if (prevProps.currentVisit !== this.props.currentVisit) {
            this.setState({applied: false, recommendation: null});
            if (this.props.hidden) this.analyzeStale = true;
            else this.runAnalyze();
        }

        if (prevProps.hidden && !this.props.hidden) {
            // The offset was not tracked while closed; the page may have scrolled since.
            this.trackHeaderOffset();
            if (this.analyzeStale) {
                this.analyzeStale = false;
                this.runAnalyze();
            }
        }
    }

    runAnalyze = () => {
        const {emr, currentVisit} = this.props;
        if (!emr || !currentVisit) return;

        this.setState({loading: true, error: null});
        RecommenderService.analyze(
            {record: emr, visitId: currentVisit.id},
            (data) => this.setState({
                loading: false, analysis: data,
                answers: {}, accepted: {}, confirmed: {}, expanded: {},
            }),
            (err) => this.setState({loading: false, error: errorMessage(err)})
        );
    };

    loadRecommendation = () => {
        const {emr, currentVisit} = this.props;
        this.setState({recommendLoading: true, recommendError: null});
        RecommenderService.recommend(
            {record: emr, visitId: currentVisit.id},
            (data) => this.setState({recommendLoading: false, recommendation: data}),
            (err) => this.setState({recommendLoading: false, recommendError: errorMessage(err)})
        );
    };

    // --- Draft: free text (typed or dictated) -> structured record fields ---

    // Dictation lands in whichever section is open — switching the accordion switches
    // where the words go, which is the whole "one window, three boxes" flow.
    appendDictated = (chunk) => {
        if (!chunk) return;
        this.setState(state => {
            const key = state.activeDraftSection;
            const existing = state.draftSections[key] || "";
            const separator = !existing || /\s$/.test(existing) ? "" : " ";
            return {draftSections: {...state.draftSections, [key]: `${existing}${separator}${chunk}`}};
        });
    };

    setDraftSection = (key, value) => {
        this.setState(state => ({draftSections: {...state.draftSections, [key]: value}}));
    };

    // The three boxes are a writing aid, not three requests: the parse gets one text,
    // with headings, and the scribe prompt routes misplaced facts to the right fields.
    combinedDraftText = () => {
        const {subjective, objective, treatment} = this.state.draftSections;
        return [
            subjective.trim() && `SUBJECTIVE\n${subjective.trim()}`,
            objective.trim() && `OBJECTIVE\n${objective.trim()}`,
            treatment.trim() && `TREATMENT\n${treatment.trim()}`,
        ].filter(Boolean).join("\n\n");
    };

    // Composed from the note alone — the record is deliberately NOT sent. The review list
    // is only meaningful if emr_json is a parse of what was just written; passing the
    // record would have the service echo back fields nobody dictated.
    generateFromDraft = () => {
        const text = this.combinedDraftText();
        if (!text) return;

        this.setState({composing: true, composeNote: "", composeSections: null, composeUnmapped: []});
        RecommenderService.compose(
            {text},
            (result) => {
                const emrJson = result?.emr_json || {};
                const {sections, matched, notReturned} = buildComposeRows(emrJson, this.props.currentVisit);

                // The note itself is only ever offered when the record has no complaint
                // text yet: an existing complaint is the clinician's own wording and the
                // panel must not overwrite it. Only the Subjective box qualifies — the
                // objective/treatment text is not a complaint.
                const subjectiveText = this.state.draftSections.subjective.trim();
                const recorded = ObjectUtil.get(this.props.currentVisit, "subjective.complaint.complaint");
                if (isEmptyComposeValue(recorded) && subjectiveText) {
                    sections.unshift({
                        title: "Complaint text",
                        tab: "subjective",
                        rows: [{
                            field: {label: "Complaint (free text)"},
                            path: "subjective.complaint.complaint",
                            label: "Complaint (free text)",
                            oldValue: recorded, newValue: subjectiveText,
                            overwrites: false, multi: false, decision: null,
                        }],
                    });
                }

                const rowCount = sections.reduce((sum, s) => sum + s.rows.length, 0);
                this.setState({
                    composing: false,
                    composedPaths: allComposePaths(emrJson),
                    composeSections: sections,
                    composeMatched: matched,
                    composeNotReturned: notReturned,
                    composeUnmapped: unmappedComposePaths(emrJson),
                    composeNote: rowCount ? "" : "Nothing to change — the record already holds what the note says.",
                });
            },
            (err) => this.setState({composing: false, composeNote: errorMessage(err)})
        );
    };

    // One explicit action per row: Discard, Keep Both or Replace/Add. Clicking the same
    // button again withdraws the decision — a mis-click must be as reversible as a tick was.
    setComposeRowDecision = (sectionIndex, rowIndex, decision) => {
        this.setState(state => ({
            composeSections: state.composeSections.map((section, si) => si !== sectionIndex ? section : {
                ...section,
                rows: section.rows.map((row, ri) => ri !== rowIndex ? row
                    : {...row, decision: row.decision === decision ? null : decision}),
            }),
        }));
    };

    acceptedComposeRows = () =>
        (this.state.composeSections || []).flatMap(section =>
            section.rows.filter(row => row.decision && row.decision !== "discard"));

    undecidedComposeCount = () =>
        (this.state.composeSections || []).reduce((sum, section) =>
            sum + section.rows.filter(row => !row.decision).length, 0);

    // Accepted values go straight into currentVisit — the same object the visit tabs render
    // from — and then the panel moves to Check and re-analyzes. A dictated note always
    // leaves holes; showing what is still missing is the other half of the same job.
    applyCompose = () => {
        const rows = this.acceptedComposeRows();
        rows.forEach(row =>
            ObjectUtil.set(this.props.currentVisit, row.path.split("."), effectiveComposeValue(row)));

        this.setState({
            composeSections: null,
            composeMatched: 0,
            composeNotReturned: [],
            composeUnmapped: [],
            composeNote: `${rows.length} field(s) applied`,
            applied: rows.length > 0 || this.state.applied,
            activeTab: rows.length > 0 ? "check" : this.state.activeTab,
        });

        if (rows.length > 0) {
            if (this.props.onApplied) this.props.onApplied();
            this.runAnalyze();
        }
    };

    discardCompose = () => {
        this.setState({
            composeSections: null, composeMatched: 0, composeNotReturned: [], composeUnmapped: [],
            composeNote: "Parse discarded",
        });
    };

    onSelectTab = (key) => {
        this.setState({activeTab: key});
        if (key === "cpt") {
            if (!this.state.recommendation && !this.state.recommendLoading) this.loadRecommendation();
            this.loadCptCatalog();   // in parallel: rows need it to know if a code is addable
        }
    };

    currentValueOf = (finding) => {
        if (!finding.target_path) return undefined;
        return ObjectUtil.get(this.props.currentVisit, finding.target_path);
    };

    setAnswer = (finding, value) => {
        this.setState(state => ({answers: {...state.answers, [findingKey(finding)]: value}}));
    };

    toggleAccepted = (finding) => {
        const key = findingKey(finding);
        this.setState(state => ({accepted: {...state.accepted, [key]: !state.accepted[key]}}));
    };

    // Collect the writable [path, value] pairs the clinician actually filled in / accepted.
    pendingWrites = () => {
        const findings = this.state.analysis?.findings || [];
        const writes = [];

        findings.forEach(f => {
            if (!f.target_path) return;    // nothing to round-trip into; shown as a manual note
            const key = findingKey(f);
            if (f.kind === "suggestion") {
                if (this.state.accepted[key]) writes.push([f.target_path, f.proposed_value]);
            } else {
                const value = this.state.answers[key];
                if (!isEmpty(value)) writes.push([f.target_path, value]);
            }
        });

        return writes;
    };

    openConfirmations = () => CONFIRM_NONE.filter(c => c.open(this.props.currentVisit));

    selectedConfirmations = () => this.openConfirmations().filter(c => this.state.confirmed[c.key]);

    pendingCount = () => this.pendingWrites().length + this.selectedConfirmations().length;

    toggleConfirmation = (entry) => {
        this.setState(state => ({confirmed: {...state.confirmed, [entry.key]: !state.confirmed[entry.key]}}));
    };

    apply = (thenSave) => {
        const writes = this.pendingWrites();
        const confirmations = this.selectedConfirmations();
        // currentVisit is the same object reference the EMR tabs render from, so writing
        // here updates the form; EMR.js bumps a key to remount children that captured it.
        writes.forEach(([path, value]) => ObjectUtil.set(this.props.currentVisit, path.split("."), value));
        confirmations.forEach(c => c.apply(this.props.currentVisit));

        this.setState({
            applied: writes.length + confirmations.length > 0,
            answers: {}, accepted: {}, confirmed: {}
        });
        if (this.props.onApplied) this.props.onApplied();

        if (thenSave) {
            if (this.props.onSaveRequested) this.props.onSaveRequested();
        } else {
            this.runAnalyze();
        }
    };

    // One-tap answers. Enum options become chips (a dropdown for 3 values is a wasted
    // click), and free-text questions carry model-drafted answers built from the record —
    // a blank textarea is where clinicians abandon the panel.
    renderChips(finding, options, {exact} = {}) {
        const key = findingKey(finding);
        const value = this.state.answers[key];
        return (
            <div className="emr-check-chips">
                {options.map(option =>
                    <button key={option} type="button"
                            className={`emr-check-chip ${value === option ? "selected" : ""}`}
                            onClick={() => this.setAnswer(finding, value === option ? undefined : option)}>
                        {option}
                    </button>
                )}
                {!exact && <span className="emr-check-chip-hint">or write your own below</span>}
            </div>
        );
    }

    renderInput(finding) {
        const key = findingKey(finding);
        const value = this.state.answers[key];
        const onChange = v => this.setAnswer(finding, v);
        const drafts = finding.answer_options || [];

        switch (finding.input_type) {
            case "select": {
                const options = finding.options || [];
                // Long option lists still deserve a searchable dropdown.
                return options.length > 0 && options.length <= 6
                    ? this.renderChips(finding, options, {exact: true})
                    : <SelectInput value={value ?? ""} options={options} onChange={onChange}/>;
            }
            case "number":
                return <NumberInput value={value ?? ""}
                                    onChange={v => onChange(isEmpty(v) ? v : Number(v))}/>;
            case "bool":
                return <RadioInput value={value === true ? "Yes" : value === false ? "No" : ""}
                                   options={["Yes", "No"]}
                                   onChange={v => onChange(v == null ? undefined : v === "Yes")}/>;
            case "text":
                return <>
                    {drafts.length > 0 && this.renderChips(finding, drafts)}
                    <TextInput value={value ?? ""} onChange={onChange}/>
                </>;
            case "textarea":
            default:
                return <>
                    {drafts.length > 0 && this.renderChips(finding, drafts)}
                    <TextareaInput value={value ?? ""} style={{width: "100%", minHeight: 56}}
                                   onChange={onChange}/>
                </>;
        }
    }

    // A finding is "settled" once the clinician has answered or accepted it: the card
    // collapses to a green one-liner so the list visibly burns down instead of staying
    // a fixed wall of open work.
    isSettled = (finding) => {
        const key = findingKey(finding);
        return finding.kind === "suggestion"
            ? !!this.state.accepted[key]
            : !isEmpty(this.state.answers[key]);
    };

    // Open the money-losing item first. Everything else starts as a one-line summary,
    // except when nothing can deny the claim -- then the worst item still leads.
    isExpanded = (finding, findings) => {
        const key = findingKey(finding);
        const override = this.state.expanded[key];
        if (override !== undefined) return override;
        if (this.isSettled(finding)) return false;
        if (finding.consequence === "deny") return true;
        const worst = Math.min(...findings.map(f => CONSEQUENCE_META[f.consequence]?.order ?? 2));
        return CONSEQUENCE_META[finding.consequence]?.order === worst && worst < 2;
    };

    toggleExpanded = (finding) => {
        const key = findingKey(finding);
        const findings = this.state.analysis?.findings || [];
        const now = this.isExpanded(finding, findings);
        this.setState(state => ({expanded: {...state.expanded, [key]: !now}}));
    };

    goToTab = (tabKey) => {
        if (this.props.onNavigate) this.props.onNavigate(tabKey);
    };

    // Provenance: which rule produced this ask, what that rule looks for, and whether it
    // was decided in code or by the model. Answers "why am I being asked this?" without
    // taking room on the card.
    renderRuleTooltip(finding) {
        if (!finding.rule_id) return null;
        const meta = CONSEQUENCE_META[finding.consequence] || CONSEQUENCE_META.audit;
        const kind = finding.rule_kind === "semantic" ? "AI-judged rule" : "Deterministic rule";

        const content = (
            <div style={{maxWidth: 300}}>
                <div><strong>{finding.rule_id}</strong> · {kind}</div>
                {finding.rule_checks && <div className="mt-1">{finding.rule_checks}</div>}
                <div className="mt-1">
                    If unfixed: {meta.label.toLowerCase()} (weight {finding.weight}/3)
                </div>
            </div>
        );

        return (
            <Tooltip title={content} placement="left">
                <span className="emr-check-why" onClick={e => e.stopPropagation()}>?</span>
            </Tooltip>
        );
    }

    renderSettledSummary(finding) {
        const key = findingKey(finding);
        const value = finding.kind === "suggestion" ? finding.proposed_value : this.state.answers[key];
        return (
            <div className="emr-check-settled" onClick={() => this.toggleExpanded(finding)}>
                <span className="emr-check-tick">✓</span>
                <span className="emr-check-settled-label">{finding.field_label}</span>
                <span className="emr-check-settled-value">{formatValue(value)}</span>
                <span className="emr-check-edit">edit</span>
            </div>
        );
    }

    renderFinding(finding, findings) {
        const meta = CONSEQUENCE_META[finding.consequence] || CONSEQUENCE_META.audit;
        const key = findingKey(finding);
        const current = this.currentValueOf(finding);
        const expanded = this.isExpanded(finding, findings);

        if (this.isSettled(finding) && !expanded) {
            return <div key={key}>{this.renderSettledSummary(finding)}</div>;
        }

        const tab = SECTION_TAB[finding.section];

        return (
            <div key={key} className={`emr-check-card ${finding.consequence} ${expanded ? "" : "collapsed"}`}>
                <div className="emr-check-head" onClick={() => this.toggleExpanded(finding)}>
                    <span className={`emr-check-dot ${finding.consequence}`}/>
                    <span className="emr-check-label">{finding.field_label}</span>
                    {this.renderRuleTooltip(finding)}
                    <Badge pill variant={meta.variant}>{meta.label}</Badge>
                    <span className="emr-check-caret">{expanded ? "▾" : "▸"}</span>
                </div>

                {!expanded &&
                    <div className="emr-check-teaser">
                        {finding.kind === "suggestion"
                            ? <>→ <span className="emr-check-proposed">{formatValue(finding.proposed_value)}</span></>
                            : finding.question}
                    </div>}

                {expanded && <>
                    {finding.kind === "suggestion" ? (
                        <>
                            {finding.evidence_quote &&
                                <div className="emr-check-evidence">
                                    <span className="emr-check-evidence-tag">from the note</span>
                                    “{finding.evidence_quote}”
                                </div>}
                            <div className="emr-check-change">
                                <span className="emr-check-old">{formatValue(current)}</span>
                                <span className="emr-check-arrow">→</span>
                                <span className="emr-check-proposed">{formatValue(finding.proposed_value)}</span>
                            </div>
                            {/* No target_path means there is no single field to write into
                                (e.g. "code the documented diagnosis" -> the Diagnosis tab). */}
                            {finding.target_path
                                ? <Button size="sm" block
                                          variant={this.state.accepted[key] ? "success" : "outline-success"}
                                          onClick={() => this.toggleAccepted(finding)}>
                                    {this.state.accepted[key] ? "✓ Accepted" : "Accept this"}
                                </Button>
                                : this.renderGoToTab(tab)}
                        </>
                    ) : (
                        <>
                            <div className="emr-check-question">{finding.question}</div>
                            <div className="emr-check-current">Now: {formatValue(current)}</div>
                            {finding.target_path
                                ? <div className="mt-1">{this.renderInput(finding)}</div>
                                : this.renderGoToTab(tab)}
                        </>
                    )}

                    {finding.reason && <div className="emr-check-reason mt-2">{finding.reason}</div>}
                </>}
            </div>
        );
    }

    renderGoToTab(tab) {
        if (!tab) return null;
        return (
            <Button size="sm" block variant="outline-secondary"
                    onClick={() => this.goToTab(tab.key)}>
                Fix in {tab.label} →
            </Button>
        );
    }

    renderConfirmations() {
        const open = this.openConfirmations();
        if (open.length === 0) return null;

        return (
            <div className="emr-check-confirm mb-2">
                <div className="emr-check-title">Quick confirmations</div>
                <div className="emr-check-reason mb-1">
                    Not claim findings — one tap each to close the record's open negatives.
                </div>
                {open.map(c =>
                    <Button key={c.key} size="sm" className="mr-1 mb-1"
                            variant={this.state.confirmed[c.key] ? "success" : "outline-secondary"}
                            onClick={() => this.toggleConfirmation(c)}>
                        {this.state.confirmed[c.key] ? "✓ " : ""}{c.label}
                    </Button>
                )}
            </div>
        );
    }

    // Three shapes, because three different things happen to the record and only one of
    // them costs anything:
    //   blank field      Several Times a Day
    //   keep both        Random  + Several Times a Day      (retained value shown plain)
    //   replace          A̶c̶u̶t̶e̶ → Subacute                  (struck through: it is leaving)
    // The strikethrough is reserved for a chosen Replace. Before a decision the old value
    // is shown plain — nothing is leaving until the clinician says so.
    renderComposeValue(row) {
        if (!row.overwrites) {
            return <span className="emr-check-proposed">{formatComposeValue(row.field, row.newValue)}</span>;
        }

        if (row.decision === "keep_both") {
            return <>
                <span className="emr-check-parse-keep">{formatComposeValue(row.field, row.oldValue)}</span>
                <span className="emr-check-parse-plus">+</span>
                <span className="emr-check-proposed">
                    {formatComposeValue(row.field, addedComposeValues(row))}
                </span>
            </>;
        }

        return <>
            <span className={row.decision === "replace" ? "emr-check-old" : "emr-check-parse-keep"}>
                {formatComposeValue(row.field, row.oldValue)}
            </span>
            <span className="emr-check-arrow">→</span>
            <span className="emr-check-proposed">{formatComposeValue(row.field, row.newValue)}</span>
        </>;
    }

    // Explicit actions instead of a tick: every recommendation is answered with Discard,
    // Keep Both (only when merging is possible) or Replace/Add. Apply stays locked until
    // no row is left undecided — the point is that nothing gets written by omission.
    renderComposeRow(row, sectionIndex, rowIndex) {
        const destructive = isDestructiveRow(row);
        const decide = decision => () => this.setComposeRowDecision(sectionIndex, rowIndex, decision);
        const stateClass = row.decision === "discard" ? "off" : (row.decision ? "" : "undecided");

        return (
            <div key={row.path}
                 className={`emr-check-parse-row ${stateClass} ${destructive ? "destructive" : ""}`}>
                <span className="emr-check-parse-body">
                    <span className="emr-check-parse-label">{row.label}</span>
                    <span className="emr-check-parse-value">{this.renderComposeValue(row)}</span>
                </span>
                <span className="emr-check-parse-mode">
                    <button type="button" className={row.decision === "discard" ? "on discard" : ""}
                            onClick={decide("discard")}>Discard</button>
                    {row.overwrites && row.multi &&
                        <button type="button" className={row.decision === "keep_both" ? "on" : ""}
                                onClick={decide("keep_both")}>Keep Both</button>}
                    <button type="button" className={row.decision === "replace" ? "on" : ""}
                            onClick={decide("replace")}>{row.overwrites ? "Replace" : "Add"}</button>
                </span>
            </div>
        );
    }

    renderComposeReview() {
        const sections = this.state.composeSections;
        const accepted = this.acceptedComposeRows().length;
        const undecided = this.undecidedComposeCount();
        const total = sections.reduce((sum, s) => sum + s.rows.length, 0);
        const overwrites = sections.flatMap(s => s.rows).filter(r => r.overwrites).length;
        const additions = total - overwrites;
        const {composeMatched, composeNotReturned} = this.state;

        return (
            <div className="emr-check-parse">
                {/* The header answers the two questions a list of proposed edits raises before
                    any of them can be read: how much of this is new, and how much of it
                    overwrites me. The count is split rather than totalled, because those two
                    kinds of row are reviewed completely differently. */}
                <div className="emr-check-parse-header">
                    <div className="emr-check-parse-header-top">
                        <span className="emr-check-parse-count">{total}</span>
                        <span className="emr-check-parse-headline">
                            suggestion{total === 1 ? "" : "s"} to review
                            <span className="emr-check-parse-sub">
                                Choose an action on every row. Apply unlocks when nothing is
                                left undecided; nothing is written to the record until then.
                            </span>
                        </span>
                    </div>
                    <div className="emr-check-parse-legend">
                        {additions > 0 &&
                            <span className="emr-check-parse-legend-item">
                                <i className="dot new"/>
                                <b>{additions}</b> add{additions === 1 ? "s" : ""} without removing
                                anything
                            </span>}
                        {overwrites > 0 &&
                            <span className="emr-check-parse-legend-item">
                                <i className="dot over"/>
                                <b>{overwrites}</b> touch{overwrites === 1 ? "es" : ""} a value you
                                already entered
                            </span>}
                    </div>
                </div>

                {sections.map((section, sectionIndex) =>
                    <div key={section.title} className="mb-2">
                        {/* No all/none shortcuts on purpose: the whole point of the action
                            buttons is one deliberate decision per recommendation. */}
                        <div className="emr-check-parse-head">
                            <span>{section.title}</span>
                        </div>
                        {section.rows.map((row, rowIndex) => this.renderComposeRow(row, sectionIndex, rowIndex))}
                    </div>
                )}

                {/* The two things a list of changes cannot show. "Agreed with the record" and
                    "the note said nothing about this" both look like absence, and only one of
                    them means the field is safe to leave alone. */}
                <div className="emr-check-parse-summary">
                    {composeMatched > 0 &&
                        <div>✓ {composeMatched} field(s) already match the record.</div>}
                    {composeNotReturned?.length > 0 &&
                        <div>
                            ○ Not found in the note: {composeNotReturned.join(", ")}. These are
                            untouched — fill them on the form if they apply.
                        </div>}
                </div>

                {/* Returned by the service but not on this panel's allowlist. Said out loud
                    rather than dropped quietly: the count is what tells us ComposeFields.js
                    needs extending, and it stops the review reading as "that was all". */}
                {this.state.composeUnmapped.length > 0 &&
                    <div className="emr-check-reason mb-2">
                        {this.state.composeUnmapped.length} more field(s) were parsed that this panel
                        cannot review yet ({this.state.composeUnmapped.slice(0, 6).join(", ")}
                        {this.state.composeUnmapped.length > 6 ? ", …" : ""}). Enter those on the form.
                    </div>}

                <div className="emr-check-actions">
                    <Button block variant="success" disabled={undecided > 0} onClick={this.applyCompose}>
                        {undecided > 0
                            ? `${undecided} row${undecided === 1 ? "" : "s"} still need${undecided === 1 ? "s" : ""} a decision`
                            : (accepted > 0 ? `Apply ${accepted} field(s)` : "Finish — nothing kept")}
                    </Button>
                    <div className="d-flex justify-content-between mt-1">
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.generateFromDraft}>Re-generate</Button>
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.discardCompose}>Discard parse</Button>
                    </div>
                </div>
            </div>
        );
    }

    // One window, three boxes: the accordion keeps everything on one page (no record-tab
    // hopping, no scrolling), and the guideline panel beside it always talks about the
    // box that is open. Dictate top to bottom, then one Generate.
    renderDraftSections() {
        const {draftSections, activeDraftSection} = this.state;
        const combinedText = this.combinedDraftText();
        const sections = [
            {key: "subjective", title: "Subjective",
             placeholder: "What the patient reports — e.g. dull low back pain since 3 weeks, gradual onset, worse on sitting, 6/10, no red flags, no known allergies…"},
            {key: "objective", title: "Objective",
             placeholder: "What you found — e.g. lumbar flexion 40°, SLR negative, palpation unremarkable, gait normal, BP 120/80…"},
            {key: "treatment", title: "Treatment & Plan",
             placeholder: "What you did and the plan — e.g. manual therapy lumbar 15 min, responded well, plan 2x/week for 3 weeks, 6 sessions, goal return to sport…"},
        ];

        return sections.map(section => {
            const open = activeDraftSection === section.key;
            const value = draftSections[section.key] || "";
            return (
                <div key={section.key} className={`emr-check-draft-section ${open ? "open" : ""}`}>
                    <button type="button" className="emr-check-draft-section-head"
                            onClick={() => this.setState({activeDraftSection: section.key})}>
                        <span className="emr-check-draft-section-caret">{open ? "▾" : "▸"}</span>
                        <span className="emr-check-draft-section-title">{section.title}</span>
                        {!open && value.trim() &&
                            <span className="emr-check-draft-section-preview">{value.trim()}</span>}
                    </button>
                    {open && <>
                        <div className="emr-check-draft-wrap">
                            <TextareaInput value={value} className="emr-check-draft"
                                           placeholder={section.placeholder}
                                           onChange={v => this.setDraftSection(section.key, v)}/>
                            {/* The mic sits inside the open box: dictation lands where you are. */}
                            <Dictation onAppend={this.appendDictated}/>
                        </div>
                        {/* The cheat-sheet lives inside the open box, right under where the
                            words land. It still reads the whole draft, not just this box —
                            a fact stated in the "wrong" box counts, same as the parse. */}
                        <GuidelinePanel section={section.key}
                                        text={combinedText}
                                        composedPaths={this.state.composedPaths}/>
                    </>}
                </div>
            );
        });
    }

    renderDraftTab() {
        const text = this.combinedDraftText();

        return (
            <div className="pt-2">
                {this.renderDraftSections()}

                <div className="emr-check-draft-actions">
                    {this.state.composeNote &&
                        <span className="emr-check-reason">{this.state.composeNote}</span>}
                    {/* Not a bootstrap variant: this is the one action on the tab and it is
                        what the drawer is for, so it carries the CoPilot accent rather than
                        looking like every other primary button on the record. */}
                    <button type="button"
                            className={"emr-check-generate" + (this.state.composing ? " busy" : "")}
                            disabled={this.state.composing || !text.trim()}
                            onClick={this.generateFromDraft}>
                        <span className="emr-check-generate-spark">
                            {this.state.composing
                                ? <span className="emr-check-generate-spin"/>
                                : "✦"}
                        </span>
                        {this.state.composing ? "Generating…" : "Generate EMR"}
                    </button>
                </div>

                {this.state.composing && <LoadingSteps messages={COMPOSE_STEPS}/>}
                {!this.state.composing && this.state.composeSections?.length > 0 && this.renderComposeReview()}

                {this.state.applied &&
                    <div className="emr-check-unsaved mt-2">⚠ Applied to the form — not saved yet.</div>}
            </div>
        );
    }

    renderCheckTab() {
        const {loading, error, analysis} = this.state;

        if (loading) return <LoadingSteps messages={CHECK_STEPS}/>;
        if (error) return <div className="p-2 text-danger">{error}</div>;
        if (!analysis) return null;

        const findings = analysis.findings || [];
        const open = findings.filter(f => !this.isSettled(f));
        const pending = this.pendingCount();

        return (
            <div>
                {this.renderHeadline(analysis, open)}

                {findings.length === 0 &&
                    <div className="emr-check-clean">
                        <div className="emr-check-clean-tick">✓</div>
                        <div>
                            <strong>Clean claim</strong>
                            <div className="emr-check-reason">
                                {analysis.checked} rule(s) checked against the billed codes —
                                nothing to fix.
                            </div>
                        </div>
                    </div>}

                {findings.map(f => this.renderFinding(f, findings))}

                {/* The cap must not read as "that was all": the backend ranks and keeps
                    the highest-impact findings, and says how many it held back. */}
                {analysis.findings_omitted > 0 &&
                    <div className="emr-check-reason mb-2">
                        + {analysis.findings_omitted} lower-priority item(s) held back —
                        fix the above and re-check to see them.
                    </div>}

                {this.renderConfirmations()}

                {this.state.applied &&
                    <div className="emr-check-unsaved">⚠ Applied to the form — not saved yet.</div>}

                <div className="emr-check-actions">
                    <Button block variant="success" disabled={pending === 0}
                            onClick={() => this.apply(true)}>
                        {pending > 0 ? `Apply ${pending} & Save` : "Nothing to apply"}
                    </Button>
                    <div className="d-flex justify-content-between mt-1">
                        <Button size="sm" variant="link" className="px-0"
                                onClick={this.runAnalyze}>Re-check</Button>
                        <Button size="sm" variant="link" className="px-0" disabled={pending === 0}
                                onClick={() => this.apply(false)}>Apply without saving</Button>
                    </div>
                </div>
            </div>
        );
    }

    // No billed amount and no numeric score in front of the clinician: a "100/100"
    // or an AED figure reads as a promise the insurer will approve, which nobody can
    // make. The clinician gets the three-state headline; the score stays API-only.
    renderHeadline(analysis, open) {
        const blocking = open.filter(f => f.consequence === "deny").length;

        let tone = "ok";
        let text = "Documentation supports the claim";
        if (blocking > 0) {
            tone = "bad";
            text = `Claim can be denied — ${blocking} blocking issue${blocking > 1 ? "s" : ""}`;
        } else if (open.length > 0) {
            tone = "warn";
            text = `${open.length} issue${open.length > 1 ? "s" : ""} weakening the claim`;
        }

        return (
            <div className={`emr-check-headline ${tone}`}>
                <div className="emr-check-headline-text">{text}</div>
                <div className="emr-check-headline-sub">
                    {tone === "ok" && <span>Documentation check only — not a guarantee of insurance approval</span>}
                    {analysis.semantic === false && <span className="ml-2">(quick check)</span>}
                </div>
            </div>
        );
    }

    // Both engines answer the same question, so they belong in one list; where a code
    // came from is a property of the row (a badge), not a reason to split the screen.
    // Codes already on the record are dropped entirely — the tab is only about what the
    // clinician has NOT recorded yet.
    mergeRecommendations(lists) {
        const recorded = recordedCptCodes(this.props.currentVisit);
        const merged = new Map();

        lists.forEach(({items, source}) => {
            (items || []).forEach(item => {
                const code = String(item?.code || "").trim();
                if (!code || code === "N/A" || recorded.has(code)) return;
                const existing = merged.get(code) || {code, sources: []};
                merged.set(code, {
                    ...existing,
                    description: existing.description || item.description || item.name || "",
                    reason: existing.reason || item.reason,
                    is_valid: item.is_valid === false ? false : existing.is_valid,
                    validation_warning: existing.validation_warning || item.validation_warning,
                    suggested_code: existing.suggested_code || item.suggested_code,
                    sources: existing.sources.includes(source)
                        ? existing.sources : [...existing.sources, source],
                });
            });
        });

        return [...merged.values()];
    }

    // Which list a new procedure belongs in. Insurance decides first: the catalogue
    // endpoint returns nothing for DIRECT_BILLING scope unless insurance/company is
    // passed, so an uninsured patient can only be billed self-pay anyway. With insurance,
    // follow whichever table this visit already uses.
    billTypeForVisit() {
        if (!ObjectUtil.get(this.props.emr, "patient.insurance")) return "SP";
        const visit = this.props.currentVisit;
        if ((ObjectUtil.get(visit, "diagnosis.selfPayProcedures") || []).length > 0
            && (ObjectUtil.get(visit, "diagnosis.directBillingProcedures") || []).length === 0) {
            return "SP";
        }
        return "DB";
    }

    // A procedure must carry the real catalogue CptDTO — the backend resolves it by
    // cpt.id, so a {code, description} stub from the recommender cannot be saved.
    // Same endpoint and params the Diagnosis tab's dropdown uses.
    //
    // Both scopes are loaded, not just the preferred one: the recommender knows nothing
    // about this clinic's price list, so a code may exist only self-pay or only under the
    // patient's insurance. Whichever list actually carries the code decides the table it
    // goes into. (DIRECT_BILLING returns nothing without insurance params, so that call
    // is skipped for uninsured patients rather than firing and coming back empty.)
    loadCptCatalog = () => {
        if (this.state.cptIndex || this.state.catalogLoading) return;
        this.setState({catalogLoading: true});

        const visit = this.props.currentVisit;
        const country = ObjectUtil.get(visit, "clinic.country");
        const speciality = ObjectUtil.get(visit, "appointment.staff.specialityName");
        const insurance = ObjectUtil.get(this.props.emr, "patient.insurance");
        const url = `api/cpt/code-or-description/${country}`;

        const queries = [{
            billType: "SP",
            params: {scopes: ["ALL", "SELF_PAY"], speciality},
        }];
        if (insurance) {
            queries.push({
                billType: "DB",
                params: {
                    filterWebinarAndClass: true,
                    scopes: ["ALL", "DIRECT_BILLING"],
                    speciality,
                    insurance: insurance?.tpa?.name,
                    insuranceCompanyId: insurance?.company?.id,
                },
            });
        }

        Promise.all(queries.map(q => new Promise(resolve =>
            RemotingService.getRemoteCall(url, q.params,
                catalog => resolve({billType: q.billType, catalog: catalog || []}),
                () => resolve(null))
        ))).then(results => {
            if (results.every(r => r === null)) {
                this.setState({catalogLoading: false, addError: "Could not load the CPT catalogue."});
                return;
            }
            const index = new Map();
            // Insured patients bill through DB when the code exists there, so let the DB
            // pass overwrite the self-pay entry.
            results.filter(Boolean).forEach(({billType, catalog}) =>
                catalog.forEach(cpt => index.set(String(cpt.cptCode), {cpt, billType})));
            this.setState({cptIndex: index, catalogLoading: false});
        });
    };

    addProcedure = (item) => {
        const match = this.state.cptIndex?.get(String(item.code));
        if (!match) return;      // the row renders a note instead of a button in this case

        const listPath = match.billType === "DB"
            ? ["diagnosis", "directBillingProcedures"]
            : ["diagnosis", "selfPayProcedures"];
        const visit = this.props.currentVisit;
        const list = ObjectUtil.get(visit, listPath.join(".")) || [];
        // Not approved by default: "select for billing" stays the clinician's call,
        // and only approved procedures get service times and treatment mapping.
        list.push({type: match.billType, quantity: 1, approved: false, cpt: match.cpt});
        ObjectUtil.set(visit, listPath, list);

        this.setState({applied: true});
        if (this.props.onApplied) this.props.onApplied();
    };

    // The recommender proposes codes from clinical similarity; it cannot know this
    // clinic's price list. Say so up front instead of offering a button that fails —
    // e.g. 97140 exists for Physiotherapist and Chiropractor but not for a Physical
    // Medicine & Rehabilitation Consultant, whose catalogue simply does not carry it.
    renderAddAction(item) {
        if (this.state.catalogLoading || !this.state.cptIndex) {
            return <div className="emr-check-reason mt-2">Checking your catalogue…</div>;
        }

        const match = this.state.cptIndex.get(String(item.code));
        if (!match) {
            const speciality = ObjectUtil.get(this.props.currentVisit, "appointment.staff.specialityName");
            return (
                <div className="emr-check-reason mt-2">
                    Not billable here — {item.code} is not in the {speciality || "provider"} catalogue
                    for this clinic.
                </div>
            );
        }

        return (
            <Button size="sm" block className="mt-2" variant="outline-success"
                    disabled={item.is_valid === false}
                    onClick={() => this.addProcedure(item)}>
                Add if performed ({match.billType === "DB" ? "direct billing" : "self-pay"})
            </Button>
        );
    }

    renderCodeRow(item, {addable} = {}) {
        return (
            <div key={item.code} className="emr-check-card audit">
                <div className="emr-check-head">
                    <span className="emr-check-label">{item.code}</span>
                    {item.sources.map(s =>
                        <Badge key={s} pill variant="light" className="emr-check-source">{s}</Badge>)}
                    {item.is_valid === false && <Badge pill variant="danger">Invalid code</Badge>}
                </div>
                <div className="mt-1">{item.description}</div>
                {item.reason && <div className="emr-check-reason mt-1">{item.reason}</div>}
                {item.validation_warning &&
                    <div className="emr-check-reason text-danger">{item.validation_warning}
                        {item.suggested_code ? ` (suggested: ${item.suggested_code})` : ""}</div>}
                {addable && this.renderAddAction(item)}
            </div>
        );
    }

    renderCptTab() {
        const {recommendLoading, recommendError, recommendation, analysis} = this.state;

        if (recommendLoading) return <LoadingSteps messages={CPT_STEPS}/>;
        if (recommendError) return <div className="p-2 text-danger">{recommendError}</div>;
        if (!recommendation) return null;

        if (recommendation.unavailable) {
            return (
                <div className="p-2">
                    <div>{recommendation.unavailable}</div>
                    {recommendation.required_fields?.length > 0 &&
                        <ul className="pl-3 mt-1">
                            {recommendation.required_fields.map(f => <li key={f}>{f}</li>)}
                        </ul>}
                </div>
            );
        }

        const openFindings = (analysis?.findings || []).length;
        const procedures = this.mergeRecommendations([
            {items: recommendation.direct_billing_procedures, source: "Similar visits"},
            {items: recommendation.direct_billing_procedures_ai, source: "AI"},
        ]);
        const diagnoses = this.mergeRecommendations([
            {items: recommendation.diagnoses, source: "Similar visits"},
            // diagnoses_ai is a single object, not a list
            {items: recommendation.diagnoses_ai ? [recommendation.diagnoses_ai] : [], source: "AI"},
        ]);

        return (
            <div className="pt-2">
                {openFindings > 0 &&
                    <div className="emr-check-unsaved">
                        ⚠ {openFindings} open documentation issue(s) — these suggestions rest on an
                        incomplete record.
                    </div>}

                {this.state.addError && <div className="text-danger mb-2">{this.state.addError}</div>}
                {this.state.applied &&
                    <div className="emr-check-unsaved">⚠ Added to the record — not saved yet.</div>}

                {/* Diagnosis first, procedures after (2026-08-17 review): the diagnosis
                    is what grounds every procedure's medical necessity, so it is the
                    decision to make before adding codes. */}
                {diagnoses.length > 0 && <div className="emr-check-title mt-1">Diagnoses not yet recorded</div>}
                {diagnoses.map(d => this.renderCodeRow(d))}
                {diagnoses.length > 0 &&
                    <div className="emr-check-reason">Add these in Diagnosis & Billing — a claim needs
                        a principal diagnosis, which is the clinician's call.</div>}

                {procedures.length > 0 && <div className="emr-check-title mt-2">Procedures not yet recorded</div>}
                {procedures.map(p => this.renderCodeRow(p, {addable: true}))}

                {procedures.length === 0 && diagnoses.length === 0 &&
                    <div className="emr-check-clean">
                        <div className="emr-check-clean-tick">✓</div>
                        <div>
                            <strong>Nothing missing</strong>
                            <div className="emr-check-reason">
                                Every recommended code is already on this visit.
                            </div>
                        </div>
                    </div>}

                <div className="emr-check-actions">
                    <Button block variant="success" disabled={!this.state.applied}
                            onClick={() => this.props.onSaveRequested && this.props.onSaveRequested()}>
                        Save visit
                    </Button>
                </div>
            </div>
        );
    }

    // The finding count rides on the tab, not just the button that opened the drawer:
    // the drawer lands on Draft, so Check has to advertise its own work from across the room.
    renderCheckTabTitle() {
        const findings = this.state.analysis?.findings || [];
        const open = findings.filter(f => !this.isSettled(f)).length;
        return (
            <>
                EMR Check
                {open > 0 && <span className="badge badge-pill badge-danger ml-1">{open}</span>}
            </>
        );
    }

    render() {
        const {wide} = this.state;

        // Hidden rather than unmounted: this component keeps its state (the note, the parse,
        // the review ticks) while it renders nothing. See requestClose.
        if (this.props.hidden) return null;

        return (
            <div className={`emr-check-panel ${wide ? "wide" : ""}`} style={{top: this.state.topOffset}}>
                <div className="emr-check-drawer-header">
                    <span className="emr-check-title">EMR CoPilot</span>
                    <span>
                        <button type="button" className="emr-check-close mr-1"
                                title={wide ? "Narrow the drawer" : "Widen the drawer"}
                                onClick={() => this.setState(s => ({wide: !s.wide}))}>
                            {wide ? "⇥" : "⇤"}
                        </button>
                        <button type="button" className="emr-check-close" onClick={this.requestClose}>
                            Close ✕
                        </button>
                    </span>
                </div>

                <div className="emr-check-drawer-body">
                    <Tabs id="emr-check-tabs" activeKey={this.state.activeTab} onSelect={this.onSelectTab}>
                        {/* "Today's Visit", not "Draft": the tab name is the instruction —
                            write (or dictate) what happened today, then Generate. */}
                        <Tab eventKey="draft" title="Today's Visit">{this.renderDraftTab()}</Tab>
                        <Tab eventKey="check" title={this.renderCheckTabTitle()}>{this.renderCheckTab()}</Tab>
                        <Tab eventKey="cpt" title="CPT Recommender">{this.renderCptTab()}</Tab>
                    </Tabs>
                </div>
            </div>
        );
    }
}
